// Metas multi-área: grupos (áreas da vida) → metas → submetas.
// Várias metas ativas ao mesmo tempo; a "destaque ★" alimenta a Probabilidade
// e o planner. Metas numéricas (peso, R$) têm check-in → evento goal.progress.

import type { Goal, GoalArea, GoalStatus, GoalType } from '@app-types/domain';
import {
  GOAL_AREAS,
  areaOf,
  childrenOf,
  daysToTarget,
  goalProgress,
  hoursByArea,
  rootsOfArea,
} from '@core/goals';
import { goalActions } from '@core/store';
import { Modal } from '@ui/components/Modal';
import { ProbabilityCard } from '@ui/components/ProbabilityCard';
import { toast } from '@ui/components/Toast';
import { useStore } from '@ui/lib/useStore';
import { useState } from 'preact/hooks';

function DeadlineChip(props: { goal: Goal }) {
  const d = daysToTarget(props.goal);
  if (d === null) return null;
  const cls = d < 0 ? 'tag red' : d <= 7 ? 'tag red' : d <= 30 ? 'tag amber' : 'tag dim';
  const text = d < 0 ? `passou há ${-d}d` : d === 0 ? 'é HOJE' : `${d}d restantes`;
  return (
    <span class={cls} style={{ fontSize: '9px' }}>
      ⏳ {text}
    </span>
  );
}

function ProgressBar(props: { pct: number | null; priority: 1 | 2 | 3 }) {
  if (props.pct === null) return null;
  return (
    <div
      style={{
        height: '6px',
        background: 'rgba(0,8,24,0.6)',
        borderRadius: '3px',
        overflow: 'hidden',
        border: '1px solid var(--border-soft)',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${props.pct}%`,
          background:
            props.priority === 1
              ? 'linear-gradient(90deg, var(--pink), var(--purple))'
              : 'linear-gradient(90deg, var(--cyan), var(--purple))',
          transition: 'width 0.5s ease',
        }}
      />
    </div>
  );
}

const STATUS_TAG: Record<GoalStatus, { cls: string; label: string }> = {
  active: { cls: 'tag green', label: 'ativa' },
  paused: { cls: 'tag amber', label: 'pausada' },
  achieved: { cls: 'tag', label: '✓ atingida' },
  abandoned: { cls: 'tag dim', label: 'abandonada' },
};

export function GoalsScreen() {
  const goals = useStore((s) => s.goals);
  const contents = useStore((s) => s.contents);
  const sessions = useStore((s) => s.sessions);
  const settings = useStore((s) => s.settings);
  const [editing, setEditing] = useState<Goal | null>(null);
  const [creating, setCreating] = useState(false);
  const [presetParent, setPresetParent] = useState<Goal | null>(null);

  const activeGoal = goals.find((g) => g.id === settings.active_goal_id && g.status === 'active');
  const areaHours = hoursByArea(goals, sessions);
  const areasWithGoals = GOAL_AREAS.filter((a) => goals.some((g) => areaOf(g) === a.id));

  function checkIn(g: Goal) {
    const unit = g.unit ? ` (${g.unit})` : '';
    const raw = prompt(
      `Check-in de "${g.title}"${unit} — valor atual:`,
      String(g.current_value ?? g.start_value ?? ''),
    );
    if (raw === null) return;
    const value = Number.parseFloat(raw.replace(',', '.'));
    if (Number.isNaN(value)) {
      toast('Valor inválido', 'err');
      return;
    }
    goalActions.checkIn(g.id, value);
    const done =
      g.target_value !== undefined &&
      g.start_value !== undefined &&
      (g.target_value >= g.start_value ? value >= g.target_value : value <= g.target_value);
    toast(
      done ? '🏆 Alvo atingido! Marque a meta como atingida.' : '✓ Check-in registrado',
      done ? 'ok' : 'info',
    );
  }

  function GoalRow(props: { goal: Goal; nested?: boolean }) {
    const g = props.goal;
    const prog = goalProgress(g, goals, contents, sessions);
    const isDestaque = settings.active_goal_id === g.id;
    const isNumeric = g.target_value !== undefined && g.start_value !== undefined;
    return (
      <div
        style={{
          padding: props.nested ? '10px 12px' : '0',
          background: props.nested ? 'rgba(0,8,24,0.4)' : 'transparent',
          border: props.nested ? '1px solid var(--border-soft)' : 'none',
          borderRadius: 'var(--r-sm)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '6px',
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: '120px',
              fontSize: props.nested ? '13px' : '15px',
              color: 'var(--text)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            onClick={() => setEditing(g)}
            title="Editar"
          >
            {isDestaque && <span style={{ color: 'var(--gold, #ffd700)' }}>★ </span>}
            {g.title}
          </span>
          <DeadlineChip goal={g} />
          <span class={STATUS_TAG[g.status].cls} style={{ fontSize: '9px' }}>
            {STATUS_TAG[g.status].label}
          </span>
        </div>
        <ProgressBar pct={prog.pct} priority={g.priority} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            marginTop: '5px',
            fontFamily: 'var(--f-mono)',
            fontSize: '10px',
            color: 'var(--text-dim)',
          }}
        >
          <span>
            {prog.label}
            {prog.pct !== null && ` · ${prog.pct}%`}
          </span>
          <span style={{ display: 'flex', gap: '6px' }}>
            {isNumeric && g.status === 'active' && (
              <button class="btn sm ghost" style={{ fontSize: '9px' }} onClick={() => checkIn(g)}>
                + CHECK-IN
              </button>
            )}
            {!isDestaque && g.status === 'active' && (
              <button
                class="btn sm ghost"
                style={{ fontSize: '9px' }}
                title="Meta destaque: alimenta a Probabilidade e o planner"
                onClick={() => {
                  goalActions.setActive(g.id);
                  toast('★ Meta em destaque', 'ok');
                }}
              >
                ★
              </button>
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div class="main-head">
        <div>
          <h1 class="main-title">Metas</h1>
          <div class="main-sub">
            {goals.filter((g) => g.status === 'active').length} ativas · {areasWithGoals.length}{' '}
            áreas da vida
          </div>
        </div>
        <div class="main-actions">
          <button class="btn primary" onClick={() => setCreating(true)}>
            + NOVA META
          </button>
        </div>
      </div>

      {/* distribuição de horas por área */}
      {areasWithGoals.length > 1 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {areasWithGoals.map((a) => (
            <span key={a.id} class="tag" style={{ fontSize: '10px', padding: '5px 10px' }}>
              {a.icon} {a.label} · {areaHours[a.id].toFixed(1)}h
            </span>
          ))}
        </div>
      )}

      {activeGoal && <ProbabilityCard goal={activeGoal} />}

      {goals.length === 0 ? (
        <div class="panel">
          <div class="empty-state">
            <div class="ico">🎯</div>
            <h3>Nenhuma meta criada</h3>
            <div>
              Organize sua vida em áreas: <b>Carreira</b> (certificações), <b>Educação</b>{' '}
              (faculdade), <b>Saúde</b>, <b>Finanças</b>...
              <br />
              Crie grupos (ex: "Certificações 2026") com submetas (CPA-20, CEA, CFP).
            </div>
            <button
              class="btn primary lg"
              style={{ marginTop: '20px' }}
              onClick={() => setCreating(true)}
            >
              🎯 CRIAR PRIMEIRA META
            </button>
          </div>
        </div>
      ) : (
        areasWithGoals.map((area) => {
          const roots = rootsOfArea(goals, area.id);
          if (roots.length === 0) return null;
          return (
            <div key={area.id} style={{ marginBottom: '26px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px',
                  paddingBottom: '6px',
                  borderBottom: '1px solid var(--border-soft)',
                }}
              >
                <span style={{ fontSize: '16px' }}>{area.icon}</span>
                <span
                  style={{
                    fontFamily: 'var(--f-heading)',
                    fontSize: '13px',
                    letterSpacing: '2px',
                    color: area.color,
                  }}
                >
                  {area.label}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--f-mono)',
                    fontSize: '10px',
                    color: 'var(--text-dim)',
                  }}
                >
                  {roots.length} meta{roots.length > 1 ? 's' : ''}
                  {areaHours[area.id] > 0 && ` · ${areaHours[area.id].toFixed(1)}h investidas`}
                </span>
              </div>

              <div class="grid-2">
                {roots.map((g) => {
                  const kids = childrenOf(goals, g.id);
                  return (
                    <div key={g.id} class="card hover" style={{ padding: '16px' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '8px',
                        }}
                      >
                        <span
                          class={
                            'tag ' +
                            (g.priority === 1 ? 'pink' : g.priority === 2 ? 'purple' : 'dim')
                          }
                          style={{ fontSize: '9px' }}
                        >
                          {g.priority === 1 ? 'PRINCIPAL' : g.priority === 2 ? 'ALTA' : 'MÉDIA'}
                        </span>
                        {kids.length > 0 && (
                          <span class="tag" style={{ fontSize: '9px' }}>
                            GRUPO · {kids.length}
                          </span>
                        )}
                      </div>

                      <GoalRow goal={g} />

                      {g.description && (
                        <div
                          style={{
                            fontSize: '11.5px',
                            color: 'var(--text-dim)',
                            margin: '8px 0 0',
                            lineHeight: 1.5,
                          }}
                        >
                          {g.description}
                        </div>
                      )}

                      {kids.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            marginTop: '12px',
                          }}
                        >
                          {kids.map((k) => (
                            <GoalRow key={k.id} goal={k} nested />
                          ))}
                        </div>
                      )}

                      <div style={{ marginTop: '12px', display: 'flex', gap: '6px' }}>
                        <button
                          class="btn sm ghost"
                          style={{ flex: 1 }}
                          onClick={() => setEditing(g)}
                        >
                          EDITAR
                        </button>
                        <button
                          class="btn sm ghost"
                          style={{ flex: 1 }}
                          title="Criar submeta dentro deste grupo"
                          onClick={() => {
                            setPresetParent(g);
                            setCreating(true);
                          }}
                        >
                          + SUBMETA
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      <GoalModal
        open={creating || editing !== null}
        goal={editing}
        goals={goals}
        presetParent={presetParent}
        onClose={() => {
          setCreating(false);
          setEditing(null);
          setPresetParent(null);
        }}
      />
    </div>
  );
}

function GoalModal(props: {
  open: boolean;
  goal: Goal | null;
  goals: Goal[];
  presetParent: Goal | null;
  onClose: () => void;
}) {
  const isEdit = props.goal !== null;
  const key = (props.goal?.id || 'new') + (props.presetParent?.id || '');
  const [title, setTitle] = useState(props.goal?.title || '');
  const [description, setDescription] = useState(props.goal?.description || '');
  const [type, setType] = useState<GoalType>(props.goal?.type || 'cert');
  const [priority, setPriority] = useState<1 | 2 | 3>(props.goal?.priority || 2);
  const [status, setStatus] = useState<GoalStatus>(props.goal?.status || 'active');
  const [area, setArea] = useState<GoalArea>(
    props.goal ? areaOf(props.goal) : props.presetParent ? areaOf(props.presetParent) : 'carreira',
  );
  const [parentId, setParentId] = useState<string>(
    props.goal?.parent_id || props.presetParent?.id || '',
  );
  const [targetDate, setTargetDate] = useState(props.goal?.target_date?.slice(0, 10) || '');
  const [targetHours, setTargetHours] = useState(props.goal?.target_hours?.toString() || '');
  const [startValue, setStartValue] = useState(props.goal?.start_value?.toString() || '');
  const [targetValue, setTargetValue] = useState(props.goal?.target_value?.toString() || '');
  const [unit, setUnit] = useState(props.goal?.unit || '');

  // grupos elegíveis: metas raiz, exceto a própria
  const parentOptions = props.goals.filter((g) => !g.parent_id && g.id !== props.goal?.id);
  const num = (v: string) => (v.trim() ? Number.parseFloat(v.replace(',', '.')) : undefined);

  return (
    <Modal
      key={key}
      open={props.open}
      onClose={props.onClose}
      title={
        isEdit
          ? 'Editar meta'
          : props.presetParent
            ? `Submeta de "${props.presetParent.title}"`
            : 'Nova meta'
      }
      actions={
        <>
          {isEdit && (
            <button
              class="btn danger"
              onClick={() => {
                if (
                  confirm(
                    `Deletar "${props.goal!.title}"? Submetas viram metas raiz; conteúdos ficam sem meta.`,
                  )
                ) {
                  goalActions.remove(props.goal!.id);
                  toast('Meta removida', 'warn');
                  props.onClose();
                }
              }}
            >
              DELETAR
            </button>
          )}
          <button class="btn" onClick={props.onClose}>
            CANCELAR
          </button>
          <button
            class="btn primary"
            onClick={() => {
              if (!title.trim()) {
                toast('Título obrigatório', 'err');
                return;
              }
              const sv = num(startValue);
              const tv = num(targetValue);
              if ((sv === undefined) !== (tv === undefined)) {
                toast('Meta numérica precisa de valor inicial E alvo', 'err');
                return;
              }
              const payload = {
                title: title.trim(),
                description: description.trim() || undefined,
                type,
                priority,
                status,
                area,
                parent_id: parentId || undefined,
                target_date: targetDate ? new Date(targetDate).toISOString() : undefined,
                target_hours: targetHours ? Number.parseInt(targetHours, 10) : undefined,
                start_value: sv,
                target_value: tv,
                unit: unit.trim() || undefined,
              };
              if (isEdit) {
                goalActions.update(props.goal!.id, payload);
                toast('Meta atualizada', 'ok');
              } else {
                const g = goalActions.add(payload);
                if (priority === 1 && !g.parent_id) goalActions.setActive(g.id);
                toast('Meta criada', 'ok');
              }
              props.onClose();
            }}
          >
            {isEdit ? 'SALVAR' : 'CRIAR'}
          </button>
        </>
      }
    >
      <div class="field">
        <label>Título</label>
        <input
          class="input"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          placeholder="ex: CPA-20 ANBIMA 2026 · Emagrecer · Reserva de emergência"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <div class="field">
          <label>Área da vida</label>
          <select
            class="select"
            value={area}
            onChange={(e) => setArea((e.target as HTMLSelectElement).value as GoalArea)}
          >
            {GOAL_AREAS.map((a) => (
              <option key={a.id} value={a.id}>
                {a.icon} {a.label}
              </option>
            ))}
          </select>
        </div>
        <div class="field">
          <label>Faz parte de (grupo)</label>
          <select
            class="select"
            value={parentId}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              setParentId(v);
              const parent = parentOptions.find((g) => g.id === v);
              if (parent) setArea(areaOf(parent));
            }}
          >
            <option value="">— nenhuma (meta raiz)</option>
            {parentOptions.map((g) => (
              <option key={g.id} value={g.id}>
                {g.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div class="field">
        <label>Descrição (opcional)</label>
        <textarea
          class="textarea"
          style={{ minHeight: '50px' }}
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
        <div class="field">
          <label>Tipo</label>
          <select
            class="select"
            value={type}
            onChange={(e) => setType((e.target as HTMLSelectElement).value as GoalType)}
          >
            <option value="cert">Certificação</option>
            <option value="smart">SMART</option>
            <option value="okr">OKR</option>
            <option value="habit">Hábito</option>
          </select>
        </div>
        <div class="field">
          <label>Prioridade</label>
          <select
            class="select"
            value={priority}
            onChange={(e) =>
              setPriority(Number.parseInt((e.target as HTMLSelectElement).value, 10) as 1 | 2 | 3)
            }
          >
            <option value="1">1 · Principal</option>
            <option value="2">2 · Alta</option>
            <option value="3">3 · Média</option>
          </select>
        </div>
        <div class="field">
          <label>Status</label>
          <select
            class="select"
            value={status}
            onChange={(e) => setStatus((e.target as HTMLSelectElement).value as GoalStatus)}
          >
            <option value="active">Ativa</option>
            <option value="paused">Pausada</option>
            <option value="achieved">Atingida</option>
            <option value="abandoned">Abandonada</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <div class="field">
          <label>Prazo / data da prova (opcional)</label>
          <input
            class="input"
            type="date"
            value={targetDate}
            onInput={(e) => setTargetDate((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label>Horas-alvo (opcional)</label>
          <input
            class="input"
            type="number"
            min="0"
            value={targetHours}
            onInput={(e) => setTargetHours((e.target as HTMLInputElement).value)}
            placeholder="ex: 120"
          />
        </div>
      </div>

      <div
        style={{
          border: '1px solid var(--border-soft)',
          borderRadius: 'var(--r-sm)',
          padding: '10px 12px',
          marginTop: '4px',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: '10px',
            color: 'var(--text-dim)',
            marginBottom: '8px',
          }}
        >
          META NUMÉRICA (opcional) — peso, dinheiro, nota... progresso via check-ins
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
          <div class="field" style={{ marginBottom: 0 }}>
            <label>Valor inicial</label>
            <input
              class="input"
              inputMode="decimal"
              value={startValue}
              onInput={(e) => setStartValue((e.target as HTMLInputElement).value)}
              placeholder="ex: 90"
            />
          </div>
          <div class="field" style={{ marginBottom: 0 }}>
            <label>Valor alvo</label>
            <input
              class="input"
              inputMode="decimal"
              value={targetValue}
              onInput={(e) => setTargetValue((e.target as HTMLInputElement).value)}
              placeholder="ex: 80"
            />
          </div>
          <div class="field" style={{ marginBottom: 0 }}>
            <label>Unidade</label>
            <input
              class="input"
              value={unit}
              onInput={(e) => setUnit((e.target as HTMLInputElement).value)}
              placeholder="kg · R$"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
